import type { Dayjs } from 'dayjs'
import { useCallback, useMemo, useRef, useState } from 'react'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { cn } from '@cocoonspace/ui/lib/utils'
import {
	animate,
	motion,
	useMotionValue,
	useMotionValueEvent,
} from 'motion/react'

const DAYS_TO_DISPLAY = 7

export const DayCol = ({
	day,
	selectedDay,
	onClick,
	disabled,
}: {
	selectedDay: Dayjs
	day: Dayjs
	onClick: () => void
	disabled?: boolean
}) => {
	const isToday = day.isSame(selectedDay)

	return (
		<div style={{ minWidth: `${100 / DAYS_TO_DISPLAY}%` }}>
			<div className='flex flex-col items-center gap-y-2'>
				<p className='text-muted text-sm uppercase'>{day.format('ddd')[0]}</p>

				<button
					className={cn('h-9 w-9 rounded-full text-xs', {
						'bg-primary hover:bg-primary focus:bg-primary text-white': isToday,
						'opacity-30': disabled,
					})}
					disabled={disabled}
					onClick={onClick}
					type='button'
				>
					{day.format('DD')}
				</button>
			</div>
		</div>
	)
}

export const BookingWeekdayPicker = ({
	value,
	onChange,
	min,
	max,
	className,
}: {
	value: string
	onChange: (day: string) => void
	min?: string
	max?: string
	className?: string
}) => {
	const x = useMotionValue(0)
	const wrapperRef = useRef<HTMLDivElement>(null)
	const [xState, setXState] = useState(x.get())

	useMotionValueEvent(x, 'change', setXState)

	const days = useMemo(() => {
		const offset = 6
		const selectedDay = dateTimeUtils(value)

		return Array.from({ length: offset * 2 + 1 }, (_, i) => i - offset).map(
			(offset) => selectedDay.add(offset, 'day'),
		)
	}, [value])

	const handleDayClick = useCallback(
		(day: Dayjs, index: number) => {
			const middleDayIndex = Math.floor(days.length / 2)

			if (index === middleDayIndex || !wrapperRef.current) {
				return
			}

			animate(
				x,
				(wrapperRef.current?.offsetWidth / DAYS_TO_DISPLAY) *
					(middleDayIndex - index),
				{
					onComplete() {
						x.set(0)
						onChange(day.format('YYYY-MM-DD'))
					},
				},
			)
		},
		[days.length, onChange, x],
	)

	return (
		<div className={cn('overflow-hidden', className)}>
			<motion.div
				className='inline-flex w-full flex-nowrap justify-center'
				ref={wrapperRef}
				style={{ x: xState }}
			>
				{days.map((day, index) => (
					<DayCol
						day={day}
						disabled={
							(!!min && day.isBefore(min, 'day')) ||
							(!!max && day.isAfter(max, 'day'))
						}
						key={day.toString()}
						onClick={() => handleDayClick(day, index)}
						selectedDay={dateTimeUtils(value)}
					/>
				))}
			</motion.div>
		</div>
	)
}
